/**
 * Product Form Components
 *
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.qtyInput = this.querySelector('[data-qty-input]');
  }

  connectedCallback() {
    this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  /**
   * Get cart element dynamically - ensures it's available when needed
   * @returns {HTMLElement|null} The cart-form element
   */
  getCartElement() {
    return document.querySelector('cart-form');
  }

  updateProductDetails(container, variant) {
    (variant == false) ? this.toggleAddButton('disable') : this.toggleAddButton('enable', variant.available, variant.id);
    if(variant){
      this.updateURLandID(variant);
      this.priceUpdate(container, variant);
      this.updateImage(container, variant.featured_media);
    }
  }

  /**
   * Render Price details based on current variant
   */
   priceUpdate(container,variant){
    const priceElement = container.querySelector('[data-currentPrice]');
    if(!priceElement) return;
    
    const comparePriceElement = container.querySelector('[data-comparePrice]');
    priceElement.textContent = Utility.formatMoney(variant.price, window.globalSpace.money_format);
    if (comparePriceElement) {
      comparePriceElement.textContent = Utility.formatMoney(variant.compare_at_price, window.globalSpace.money_format);
      comparePriceElement.classList.toggle('d-none', !variant.compare_at_price || variant.compare_at_price <= 0 || variant.compare_at_price === variant.price);
    }
  }


  /**
     * Toggle the button based on product availability ( add to cart / soldOut )
     * @param {Text} status enable / disable
     * @param {Boolean} variantAvailable true/false
     */
   toggleAddButton(status, variantAvailable, variantID) {
    if (!this.submitButton) return;

    const url = `${window.location.pathname}?variant=${variantID}`;
      fetch(`${url}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if(document.querySelector(".sticky-product-addtocart") != null){
          document.querySelector(".sticky-product-addtocart").innerHTML = html.querySelector(".sticky-product-addtocart").innerHTML
        }      
      })
      .catch((error) => {
        console.error('Error updating product details:', error);
      });

    const addText = this.submitButton.querySelector('.add-text');
    if (status === 'disable' || (!variantAvailable)) {
        this.submitButton.setAttribute('disabled', true);
        if (addText) addText.textContent = status === 'disable' ? window.variantStrings.unavailable : window.variantStrings.soldOut;
    } else {
        this.submitButton.removeAttribute('disabled');
        if (addText) addText.textContent = this.form.classList.contains('cart-upsell-form') ? window.variantStrings.upsellAddText : window.variantStrings.addToCart;
    }
  }

  /**
   * Update URL on variant change event
   * @param {JSON} currentVariant 
   */
  updateURLandID(currentVariant) {
    if (!currentVariant || !this.form) return;

    if (this.dataset.format === 'product-page') window.history.replaceState({}, '', `${this.form.dataset.url}?variant=${currentVariant.id}`);
    this.form.querySelector('input[name="id"]').value = currentVariant.id;
  }

  /**
   * Render selected variant image/slide
   * @param {element} container
   * @param {object} variantMedia
   */
  updateImage(container, variantMedia) {
    if(!variantMedia) return;

    const imgElem = container.querySelector('[data-feauredImage]');
    const imageSlide = container.querySelector(`[data-productSlider] [data-mediaID="${variantMedia.id}"]`);
    if (imgElem && variantMedia.preview_image.src) {
      imgElem.removeAttribute('srcset');
      imgElem.src = variantMedia.preview_image.src;
    } else if(imageSlide) {
      const slideIndex = Array.from(imageSlide.parentNode.children).indexOf(imageSlide);
      container.querySelector('[data-productSlider]').swiper.slideTo(slideIndex, 0, false);
    }
  }

  /**
   * Check if drawer is ready (exists and custom element is upgraded)
   * @returns {Promise<boolean>} - True if drawer is ready
   */
  async isDrawerReady() {
    // Check if drawer element exists in DOM
    const drawerElement = document.querySelector('#component-cart-drawer');
    if (!drawerElement) {
      return false;
    }

    // Use customElements.whenDefined for reliable waiting
    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      try {
        // Wait for custom-drawer to be defined
        await customElements.whenDefined('custom-drawer');
        
        // Check if drawer element is upgraded and has openDrawer method
        if (typeof drawerElement.openDrawer === 'function') {
          return true;
        }
        
        // If element exists but not upgraded, wait a bit more
        return new Promise((resolve) => {
          let attempts = 0;
          const checkUpgrade = () => {
            attempts++;
            if (typeof drawerElement.openDrawer === 'function') {
              resolve(true);
            } else if (attempts < 20) {
              setTimeout(checkUpgrade, 50);
            } else {
              resolve(false);
            }
          };
          checkUpgrade();
        });
      } catch (error) {
        console.warn('Error waiting for drawer custom element:', error);
        return false;
      }
    }
    
    // Fallback: check if method exists
    return typeof drawerElement.openDrawer === 'function';
  }

  /**
   * Product Form Submit event
   *
   * @param {evt} Event instance
   */
  async onSubmitHandler(evt) {
    evt.preventDefault();

    // Prevent multiple submissions from this button
    if (this.submitButton.hasAttribute('aria-disabled') && this.submitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    // CRITICAL: Check drawer readiness BEFORE proceeding with add-to-cart
    // This ensures drawer can be opened after product is added
    if (window.globalSpace.cart_drawer) {
      const drawerReady = await this.isDrawerReady();
      if (!drawerReady) {
        console.warn('Drawer not ready, waiting...');
        // Wait up to 2 seconds for drawer to be ready
        const waitStart = Date.now();
        while ((Date.now() - waitStart) < 2000) {
          await new Promise(resolve => setTimeout(resolve, 100));
          const drawerElement = document.querySelector('#component-cart-drawer');
          if (drawerElement && typeof drawerElement.openDrawer === 'function') {
            break;
          }
        }
      }
    }

    // Check global cart operation lock - get cart element dynamically
    const cartForm = this.getCartElement();
    if (cartForm && cartForm.isCartUpdating) {
      return;
    }

    // Store original disabled state before disabling (to preserve sold-out state)
    const originalDisabledStates = new Map();
    document.querySelectorAll('product-form [type="submit"]').forEach(btn => {
      // Store if button was originally disabled (sold out) - check both attribute and property
      const wasDisabled = btn.hasAttribute('disabled') || btn.disabled === true;
      originalDisabledStates.set(btn, wasDisabled);
    });

    // Disable all add to cart buttons globally during operation
    document.querySelectorAll('product-form [type="submit"]').forEach(btn => {
      btn.setAttribute('aria-disabled', true);
      btn.disabled = true;
    });

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.submitButton.disabled = true;

    const formData = new FormData(this.form);
    // Get cart element dynamically to ensure we have the latest reference
    const cartElement = this.getCartElement();
    const sectionId = cartElement?.dataset.sectionId || 'template-cart';
    formData.append('sections', sectionId);

    // Serialize FormData to URLSearchParams
    const serializedData = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      serializedData.append(key, value);
    }

    const fetchCfg = Utility.fetchConfig('javascript', { body: serializedData });
    
    // Create a timeout promise (30 seconds)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timeout: The server took too long to respond. Please try again.'));
      }, 30000); // 30 seconds timeout
    });

    // Use cart form's queue system if available, otherwise execute directly
    const cartFormInstance = document.querySelector('cart-form');
    const executeOperation = async () => {
      try {
        const response = await Promise.race([
          fetch(routes.cart_add_url + '.js', {
            ...fetchCfg,
            headers: {
              ...fetchCfg.headers,
              Accept: 'text/html',
            },
          }),
          timeoutPromise
        ]);

        let jsonData;
        try {
          jsonData = await response.json();
        } catch (jsonError) {
          console.error('[PRODUCT-FORM] Error parsing JSON:', jsonError);
          throw new Error('Invalid server response');
        }

        if (!response.ok) {
          // Try to give a specific error message if available
          const errorMsg = (jsonData && jsonData.description) || (jsonData && jsonData.message) || (jsonData && jsonData.errors) || response.statusText || 'An unknown error occurred.';
          throw new Error(errorMsg);
        }

        if (jsonData.errors) {
          window.notificationEle.updateNotification('Add to Cart', jsonData.errors, {
            type: 'error',
            timeout: 5000
          });
          throw new Error(jsonData.errors);
        }

        eventBus.publish(PUB_SUB_EVENTS.productATC, {
          data: {
            item: jsonData
          },
        });
        document.querySelector('#PopupModal-quickshop .close-quickshop')?.dispatchEvent(new Event('click'))

        document.querySelectorAll('[data-atc-out]')?.forEach(ele => {
          ele.removeAttribute('disabled');
          ele.classList.remove('loading');
        });

        if(this.qtyInput) this.qtyInput.value = 1;
        
        // CRITICAL: Wait for cart-form custom element to be defined and ready before calling _updateCart
        // This ensures the cart-form is fully initialized and can handle drawer opening
        const waitForCartFormReady = () => {
          return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 30; // 1.5 seconds max wait
            
            const checkCartForm = () => {
              attempts++;
              
              // Check if cart-form custom element is defined
              if (typeof customElements !== 'undefined' && customElements.get('cart-form')) {
                const cartElement = document.querySelector('cart-form');
                if (cartElement && typeof cartElement._updateCart === 'function') {
                  resolve(cartElement);
                  return;
                }
              }
              
              if (attempts < maxAttempts) {
                setTimeout(checkCartForm, 50);
              } else {
                // After max attempts, try to get it anyway
                const cartElement = document.querySelector('cart-form');
                resolve(cartElement || null);
              }
            };
            
            checkCartForm();
          });
        };
        
        waitForCartFormReady().then((updatedCartElement) => {
          
          if(updatedCartElement && typeof updatedCartElement._updateCart === 'function'){
            // Check if sections are in the response
            let sectionsData = jsonData.sections;
            
            // If sections are not in jsonData (null, undefined, or empty), try to fetch them separately
            if (!sectionsData || sectionsData === null || sectionsData === undefined || (typeof sectionsData === 'object' && Object.keys(sectionsData).length === 0)) {
              console.warn('[PRODUCT-FORM] ⚠️ Sections not in response (null/undefined/empty), fetching separately...');
              const sectionId = updatedCartElement.dataset.sectionId || 'template-cart';
              
              // Use cart URL to fetch sections
              const sectionsUrl = `${window.routes.cart_fetch_url}?sections=${sectionId}`;
              
              fetch(sectionsUrl)
                .then(res => {
                  if (!res.ok) {
                    throw new Error(`Failed to fetch sections: ${res.status}`);
                  }
                  return res.json();
                })
                .then(sectionsResponse => {
                  
                  // Handle Shopify sections API response format: { sections: { sectionId: html } }
                  if (sectionsResponse && sectionsResponse.sections && sectionsResponse.sections[sectionId]) {
                    updatedCartElement._updateCart(sectionsResponse.sections, this.submitButton);
                  } else if (sectionsResponse && sectionsResponse[sectionId]) {
                    // Handle direct format: { sectionId: html }
                    const sectionsObj = {};
                    sectionsObj[sectionId] = sectionsResponse[sectionId];
                    updatedCartElement._updateCart(sectionsObj, this.submitButton);
                  } else {
                    console.warn('[PRODUCT-FORM] ❌ Could not find sections in response, redirecting to cart');
                    window.location.href = window.routes.cart_fetch_url;
                  }
                })
                .catch(err => {
                  console.error('[PRODUCT-FORM] ❌ Error fetching sections:', err);
                  window.location.href = window.routes.cart_fetch_url;
                });
            } else {
              updatedCartElement._updateCart(sectionsData, this.submitButton);
            }
          }else{
            console.warn('[PRODUCT-FORM] ❌ Cart form not ready after waiting, redirecting to cart page');
            window.location.href = window.routes.cart_fetch_url;
          }
        });

        return jsonData;
      } catch (error) {
        // Handle all errors (timeout, fetch errors, network errors, etc.)
        let errorMessage;
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error && error.errors) {
          errorMessage = error.errors;
        } else {
          errorMessage = 'An unknown error occurred.';
        }
        window.notificationEle.updateNotification('Error', errorMessage, {
          type: 'error',
          timeout: 5000
        });
        throw error;
      }
    };

    // Use cart form's queue system if available
    try {
      if (cartFormInstance && typeof cartFormInstance.executeCartOperation === 'function') {
        await cartFormInstance.executeCartOperation(executeOperation);
      } else {
        // Fallback: execute directly but check for concurrent operations
        await executeOperation();
      }
    } catch (error) {
      // Error already handled in executeOperation
      console.error('Add to cart failed:', error);
    } finally {
      
      // Re-enable all add to cart buttons, but preserve disabled state for sold-out products
      document.querySelectorAll('product-form [type="submit"]:not([data-cart-drawer-upsell-btn])').forEach(btn => {
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
        
        // Restore original disabled state (preserve sold-out state)
        const wasOriginallyDisabled = originalDisabledStates.get(btn);
        if (wasOriginallyDisabled) {
          // Keep disabled for sold-out products
          btn.disabled = true;
        } else {
          // Re-enable only if it wasn't originally disabled
          btn.disabled = false;
        }
      });

      // Re-enable current button, but preserve sold-out state
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
      
      const wasThisButtonDisabled = originalDisabledStates.get(this.submitButton);
      if (wasThisButtonDisabled) {
        // Keep disabled for sold-out products
        this.submitButton.disabled = true;
      } else {
        // Re-enable only if it wasn't originally disabled
        this.submitButton.disabled = false;
      }
    }
  }
}
customElements.define('product-form', ProductForm);
